import React, { useEffect, useState } from "react";
import { ArrowPathIcon, EyeIcon, PencilSquareIcon } from "@heroicons/react/24/outline";
import { Button, Card, CardBody, CardHeader, Divider, Input, Select, SelectItem, Textarea, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, useDisclosure, Switch } from "@nextui-org/react";
import { Controller, useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import toast from "react-hot-toast";
import { format } from "date-fns";
import { getSelectFetcher, simpanSK, putFetcherWithoutId, getFetcher, getSelectProvinceFetcher, BASE_URL } from "../../../../services/api";
import ReactSelect from "../../../elements/ReactSelect";
import useSWR from "swr";
import TembusanRegistrasi from "./information/TembusanRegistrasi";
import { dirtyInput, hasValidRole, hasValidStatus } from "../../../../services/helpers";
import ModalAlert from "../../../elements/ModalAlert";
import StatusPermohonanRegistrasi from "../../../../enums/statusRegistrasi";
import useAuth from "../../../../hooks/useAuth";
import RolesAccess from "../../../../enums/roles";
import { isResponseErrorObject } from "../../../../services/helpers";
import FormAddSK from "./FormAddSK";
import LampiranTembusan from "./LampiranTembusan";

export default function Draft({ id, dataTembusan, mutate, data }) {
  const { user, roles } = useAuth();
  const { isOpen, onOpen, onOpenChange, onClose } = useDisclosure();

  const { isOpen: isOpenModalAlert, onOpenChange: onOpenChangeModalAlert } = useDisclosure();

  const [useSrikandi, setUseSrikandi] = useState(true);

  // Store srikandi state in window object to be accessible from DetailPage
  useEffect(() => {
    window.srikandiFormat = useSrikandi;
  }, [useSrikandi]);

  const dataBerlakuDari = data?.berlaku_dari ? new Date(data.berlaku_dari) : new Date();
  const dataBerlakuSampai = data?.berlaku_sampai ? new Date(data.berlaku_sampai) : new Date();
  const dataTanggalTerbit = data?.tanggal_terbit ? new Date(data.tanggal_terbit) : undefined;
  const dataTanggalSurat = data?.tanggal_surat ? new Date(data.tanggal_surat) : undefined;

  const month = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"];

  /*
   * Pilihan Bulan memakai INDEKS 0-11 (Januari = 0), sedangkan kolom "bulan"
   * yang tersimpan berbasis 1 - onSubmit di bawah memang menambahkan 1 sebelum
   * mengirim, dan nomor surat dibentuk dari nilai itu apa adanya
   * (toRoman(bulan), sehingga 9 = IX = September).
   *
   * Dulu nilai tersimpan itu dipakai langsung sebagai indeks, jadi surat
   * bertanggal September (bulan = 9) tampil sebagai Oktober - selalu maju satu
   * bulan.
   *
   * Basis data memuat DUA konvensi: baris lama sempat menyimpan hasil
   * getMonth() yang berbasis 0. Karena itu tanggal surat didahulukan sebagai
   * sumber - itu pula yang dipakai pembuat PDF - dan kolom "bulan" hanya jadi
   * cadangan bila tanggalnya belum ada.
   *
   * Hasilnya dikembalikan sebagai STRING karena Select menyimpan kunci pilihan
   * sebagai string. Bila nilai awalnya angka, react-hook-form melihat 8 dan "8"
   * sebagai dua nilai berbeda, lalu menandai form kotor sejak dibuka - tanda
   * "Perubahan belum disimpan" muncul padahal belum ada yang disentuh.
   */
  function indeksBulanAwal() {
    if (data?.status === StatusPermohonanRegistrasi.PEMBUATAN_DRAFT_SK) {
      return String(new Date().getMonth());
    }

    if (dataTanggalSurat && !isNaN(dataTanggalSurat)) {
      return String(dataTanggalSurat.getMonth());
    }

    const tersimpan = Number(data?.bulan);
    if (Number.isFinite(tersimpan) && tersimpan >= 1 && tersimpan <= 12) {
      return String(tersimpan - 1);
    }

    return String(new Date().getMonth());
  }

  const permit = ["Berjangka", "Trasaksional", "Seumur Hidup", "Perkecualian - Transaksional"];

  const tipe_surat = ["Produsen", "Import"];

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitting, dirtyFields },
    trigger,
    reset,
    unregister,
    setValue,
    watch,
  } = useForm({
    resolver: yupResolver(
      yup
        .object()
        .shape({
          berlaku_dari: yup.date().when(["$data", "$selectedTipeSurat"], {
            is: (data, selectedTipeSurat) => {
              const subLayanan = data?.sub_layanan;
              const tipeSurat = selectedTipeSurat?.toLowerCase();
              return subLayanan === "119" || tipeSurat === "produsen";
            },
            then: (schema) => schema.typeError("Tanggal harus valid").notRequired(),
            otherwise: (schema) => schema.typeError("Tanggal harus valid").required("harus diisi"),
          }),
          berlaku_sampai: yup.date().when(["$data", "$selectedTipeSurat"], {
            is: (data, selectedTipeSurat) => {
              const subLayanan = data?.sub_layanan;
              const tipeSurat = selectedTipeSurat?.toLowerCase();
              return subLayanan === "119" || tipeSurat === "produsen";
            },
            then: (schema) => schema.typeError("Tanggal harus valid").notRequired(),
            otherwise: (schema) => schema.typeError("Tanggal harus valid").required("harus diisi"),
          }),

          nomor_surat: yup.string().when(["$roles", "$data"], {
            is: (roles, data) => {
              return roles && hasValidRole(roles, [RolesAccess.DIREKTUR, RolesAccess.SUPER_ADMIN]) && hasValidStatus(data?.status, [StatusPermohonanRegistrasi.DRAFT_SK_TANDA_TANGAN_DIREKTUR]);
            },
            then: (schema) => schema.required("Harus diisi").matches(/^[0-9]+$/, "Nomor surat harus berupa angka"),
            otherwise: (schema) => schema.notRequired(),
          }),

          tanggal_surat: yup.string().when("$roles", {
            is: (roles) => roles && hasValidRole(roles, [RolesAccess.DIREKTUR]),
            then: (schema) => schema.required("Harus diisi"),
            otherwise: (schema) => schema.notRequired(),
          }),
          pejabat_id: yup.mixed().test("required", "Pejabat wajib dipilih", function (value) {
            return value && value.value;
          }),
        })
        .required()
    ),
    context: { roles, data, selectedTipeSurat: undefined }, // Set undefined initially
    defaultValues: {
      bulan: indeksBulanAwal(),
      tahun: data?.status === StatusPermohonanRegistrasi.PEMBUATAN_DRAFT_SK ? new Date().getFullYear().toString() : (data?.tahun ?? new Date().getFullYear().toString()),
      tanggal_surat: dataTanggalSurat ? format(dataTanggalSurat, "yyyy-MM-dd") : undefined,
      nomor_surat: data?.nomor_surat || "",
      // status_izin: permit.find((word) => word.toLowerCase() === data?.status_izin?.toLowerCase()),
      status_izin: data?.status_izin || "",
      tipe_surat: tipe_surat.find((word) => word.toLowerCase() === data?.type_registrasi?.toLowerCase()),
      keterangan_sk: data?.keterangan_sk || "",
      tanggal_terbit: dataTanggalTerbit ? format(dataTanggalTerbit, "yyyy-MM-dd") : undefined,
      berlaku_dari: dataBerlakuDari ? format(dataBerlakuDari, "yyyy-MM-dd") : undefined,
      berlaku_sampai: permit.find((word) => word.toLowerCase() === data?.status_izin?.toLowerCase()) === "Seumur Hidup" ? format("9999-12-31", "yyyy-MM-dd") : format(dataBerlakuSampai, "yyyy-MM-dd"),
      nomor_notifikasi_impor: data?.nomor_notifikasi_impor || "",
      pejabat_id: { value: data?.pejabat?.id, label: data?.pejabat?.nama },
      provinceId: { value: data?.province?.id, label: data?.province?.name },
    },
  });

  const {
    register: registerModal,
    handleSubmit: handleSubmitModal,
    formState: { errors: errorsModal },
    reset: resetModal,
  } = useForm({
    resolver: yupResolver(
      yup.object().shape({
        notes: yup.string().required("Catatan wajib diisi"),
      })
    ),
  });

  const { data: dataPejabat, isLoading: isLoadingPejabat } = useSWR("/api/data-master/pejabat", getSelectFetcher);

  const { data: dataProvince, isLoading: isLoadingProvince } = useSWR("/api/location/provinces", getSelectProvinceFetcher);

  const selectedStatusIzin = watch("status_izin"); // Watch for changes in the status_izin field
  const selectedTipeSurat = watch("tipe_surat"); // Watch for changes in the status_izin field
  const selectedSubLayanan = watch("sub_layanan"); // Tambahkan ini untuk watch sub_layanan

  useEffect(() => {
    const currentContext = { roles, data, selectedTipeSurat };
    trigger();
  }, [selectedTipeSurat, roles, data, trigger]);

  useEffect(() => {
    const statusIzin = selectedStatusIzin?.toLowerCase();
    const tipeSurat = selectedTipeSurat?.toLowerCase();
    const subLayanan = data?.sub_layanan;

    console.log(selectedTipeSurat, "selectedTipeSurat");
    console.log(selectedSubLayanan, "selected SubLayanan: ");
    console.log(subLayanan, "data sub_layanan:");

    if (tipeSurat === "produsen") {
      setValue("status_izin", "Seumur Hidup", { shouldDirty: true });
      setValue("berlaku_sampai", "9999-12-31", { shouldDirty: true });
    } else if (statusIzin === "seumur hidup") {
      setValue("berlaku_sampai", "9999-12-31", { shouldDirty: true });
    }

    // Trigger validasi ulang ketika tipe surat berubah
    trigger(["status_izin", "berlaku_sampai", "berlaku_dari"]);
  }, [selectedStatusIzin, selectedTipeSurat, setValue, trigger, data?.sub_layanan]);

  async function onSubmitDirektur() {
    try {
      const formData = watch();
      const tipeSurat = selectedTipeSurat?.toLowerCase();
      const subLayanan = data?.sub_layanan;

      // Hanya check berlaku_dari dan berlaku_sampai jika bukan sub_layanan 119 atau tipe produsen
      if (!(subLayanan === "119" || tipeSurat === "produsen")) {
        if (!formData.berlaku_dari) {
          toast.error("Berlaku dari harus diisi");
          return;
        }

        if (!formData.berlaku_sampai) {
          toast.error("Berlaku sampai harus diisi");
          return;
        }
      }

      if (!formData.pejabat_id || !formData.pejabat_id.value) {
        toast.error("Pejabat harus dipilih");
        return;
      }

      const submitData = {
        id: id,
        status: StatusPermohonanRegistrasi.DRAFT_SK_TANDA_TANGAN_DIREKTUR,
        userId: user.userId,
      };
      await putFetcherWithoutId(`/api/registrasi/update-status-registrasi`, submitData);
      mutate();
      toast.success("Submit Draft SK ke Direktur berhasil!");
    } catch (error) {
      toast.error("Gagal validasi!");
    }
  }

  const toTitleCase = (str) => {
    if (!str) return "";
    return str
      .toLowerCase() // Ubah semuanya ke huruf kecil
      .split(" ") // Pecah berdasarkan spasi
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1)) // Huruf pertama kapital
      .join(" "); // Gabungkan kembali menjadi string
  };

  const onSubmit = async (data) => {
    try {
      unregister("notes");
      const filteredData = dirtyInput(dirtyFields, data);
      const dataBerlakuDari = data?.berlaku_dari && new Date(data?.berlaku_dari);
      const dataBerlakuSampai = data?.berlaku_sampai && new Date(data?.berlaku_sampai);
      const dataTanggalTerbit = data?.tanggal_terbit && new Date(data?.tanggal_terbit);

      if (filteredData.bulan !== undefined) {
        filteredData.bulan = (parseInt(filteredData.bulan) + 1).toString();
      }

      if (!filteredData.tahun) {
        filteredData.tahun = data.tahun || new Date().getFullYear().toString();
      }

      if (data.berlaku_dari) {
        filteredData.berlaku_dari = format(dataBerlakuDari, "yyyy-MM-dd");
      }
      if (data?.status_izin && data?.status_izin?.toLowerCase() === "seumur hidup") {
        unregister("provinceId");
      }
      if (data.berlaku_sampai) {
        filteredData.berlaku_sampai = format(dataBerlakuSampai, "yyyy-MM-dd");
      }
      if (filteredData.tanggal_terbit) {
        filteredData.tanggal_terbit = format(dataTanggalTerbit, "yyyy-MM-dd");
      }
      if (filteredData.tipe_surat) {
        filteredData.tipe_surat = filteredData.tipe_surat.toLowerCase();
      }
      // Perbaikan: Cek apakah provinceId adalah string dan tidak kosong
      if (data.provinceId && typeof data.provinceId === "string" && data.provinceId.trim() !== "") {
        console.log(dataProvince?.find((word) => word?.value === data.provinceId));
        const dataProvinces = data?.provinceId ? toTitleCase(dataProvince?.find((word) => word?.value === data.provinceId)?.label) : "-";
        filteredData.provinceId = data.provinceId;
        filteredData.tujuan_surat = dataProvinces;
      }
      // Atau jika provinceId berupa object dengan value property
      else if (data.provinceId && typeof data.provinceId === "object" && data.provinceId.value) {
        const dataProvinces = toTitleCase(dataProvince?.find((word) => word?.value === data.provinceId.value)?.label) || "-";
        filteredData.provinceId = data.provinceId.value;
        filteredData.tujuan_surat = dataProvinces;
      }

      if (data.pejabat_id && typeof data.pejabat_id === "object" && data.pejabat_id.value) {
        filteredData.pejabat_id = data.pejabat_id.value;
      } else if (data.pejabat_id && typeof data.pejabat_id === "string" && data.pejabat_id.trim() !== "") {
        filteredData.pejabat_id = data.pejabat_id;
      } else {
        filteredData.pejabat_id = null; // Atau nilai default lainnya jika tidak ada
      }

      await simpanSK(id, filteredData);
      mutate();
      // Reset form setelah berhasil disimpan
      // reset(data); // Reset dengan nilai default
      toast.success("Draft sk berhasil dibuat!");
    } catch (error) {
      console.log(error, "isis eror draft");
      toast.error("Gagal buat draft sk!");
    }
  };

  const submitStatusKembalikanKePIC = async (formData) => {
    try {
      const submitData = {
        id: id,
        status: StatusPermohonanRegistrasi.PEMBUATAN_DRAFT_SK,
        userId: user.userId,
        notes: formData.notes,
      };

      await putFetcherWithoutId(`/api/registrasi/update-status-registrasi`, submitData);
      onClose();
      resetModal(); // Reset form modal
      mutate();
      toast.success("Mengembalikan Draft SK Ke PIC Registrasi!");
    } catch (error) {
      if (error?.message) {
        isResponseErrorObject(error?.response?.data?.message)
          ? Object.entries(error?.response?.data?.message).forEach(([key, value]) => {
              toast.error(value);
            })
          : toast.error(error?.response?.data?.message);
      } else {
        toast.error("Gagal Mengembalikan SK Ke PIC Registrasi!");
      }
      onClose();
    }
  };

  const onSendToPIC = async () => {
    onOpen();
  };

  const hasUnsavedChanges = Object.keys(dirtyFields)?.length > 0;

  return (
    <div className="flex flex-row gap-4 py-3">
      <div className={"w-1/2"}>
        <TembusanRegistrasi
          registrasiId={id}
          existingTembusan={dataTembusan}
          status={data?.status}
          lampiranTembusan={
            <LampiranTembusan
              registrasiId={id}
              data={data}
              mutate={mutate}
              isEditable={hasValidRole(roles, [RolesAccess.SUPER_ADMIN, RolesAccess.PIC_REGISTRASI]) && hasValidStatus(data?.status, [StatusPermohonanRegistrasi.PEMBUATAN_DRAFT_SK])}
            />
          }
        />
      </div>
      <form onSubmit={handleSubmit(onSubmit)} className="w-1/2">
        <Card radius="sm" className="h-full">
          <CardHeader className="flex items-center justify-between">
            <div>
              <p className="text-md">Draft Sk</p>
              {hasUnsavedChanges && <span className="text-red-500 text-sm ml-2">Perubahan belum disimpan</span>}
            </div>
            {hasValidRole(roles, [RolesAccess.PIC_REGISTRASI, RolesAccess.SUPER_ADMIN]) && hasValidStatus(data?.status, [StatusPermohonanRegistrasi.PEMBUATAN_DRAFT_SK]) && (
              <div className="ml-auto">
                <Button
                  isDisabled={hasValidStatus(data?.status, [StatusPermohonanRegistrasi.PEMBUATAN_DRAFT_SK]) ? false : true}
                  onPress={onOpenChangeModalAlert}
                  color="warning"
                  size="sm"
                  startContent={<ArrowPathIcon className="size-4" />}
                >
                  Submit Draft SK Ke Direktur
                </Button>
              </div>
            )}
          </CardHeader>

          <Divider />
          <CardBody>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-5">
              {hasValidRole(roles, [RolesAccess.PIC_REGISTRASI, RolesAccess.SUPER_ADMIN]) && (
                <>
                  {" "}
                  <Select
                    {...register("bulan")}
                    variant="faded"
                    label="Bulan"
                    isDisabled={hasValidStatus(data?.status, [StatusPermohonanRegistrasi.PEMBUATAN_DRAFT_SK]) ? false : true}
                    placeholder="Pilih"
                    labelPlacement="outside"
                    color={errors.bulan ? "danger" : "default"}
                    isInvalid={errors.bulan}
                    errorMessage={errors.bulan && errors.bulan.message}
                  >
                    {month.map((item, index) => (
                      <SelectItem key={index} value={index}>
                        {item}
                      </SelectItem>
                    ))}
                  </Select>
                  <Input
                    {...register("tahun")}
                    variant="faded"
                    labelPlacement="outside"
                    placeholder={"..."}
                    isDisabled={hasValidStatus(data?.status, [StatusPermohonanRegistrasi.PEMBUATAN_DRAFT_SK]) ? false : true}
                    type="text"
                    label="Tahun"
                    color={errors.tahun ? "danger" : "default"}
                    isInvalid={errors.tahun}
                    errorMessage={errors.tahun && errors.tahun.message}
                  />
                </>
              )}
              {hasValidRole(roles, [RolesAccess.DIREKTUR, RolesAccess.SUPER_ADMIN]) && (
                <Input
                  {...register("nomor_surat")}
                  variant="faded"
                  labelPlacement="outside"
                  type="text"
                  label="Nomor Surat"
                  placeholder={"..."}
                  isRequired={hasValidRole(roles, [RolesAccess.DIREKTUR, RolesAccess.SUPER_ADMIN])}
                  isDisabled={hasValidStatus(data?.status, [StatusPermohonanRegistrasi.DRAFT_SK_TANDA_TANGAN_DIREKTUR]) ? false : true}
                  color={errors.nomor ? "danger" : "default"}
                  isInvalid={errors.nomor}
                  errorMessage={errors.nomor && errors.nomor.message}
                  className="col-span-2"
                  onKeyPress={(e) => {
                    // Hanya izinkan input angka
                    if (!/[0-9]/.test(e.key)) {
                      e.preventDefault();
                    }
                  }}
                />
              )}

              <Select
                {...register("tipe_surat")}
                variant="faded"
                label="Tipe Surat"
                placeholder="Pilih"
                labelPlacement="outside"
                color={errors.tipe_surat ? "danger" : "default"}
                isInvalid={errors.tipe_surat}
                errorMessage={errors.tipe_surat && errors.tipe_surat.message}
                isDisabled={hasValidRole(roles, [RolesAccess.SUPER_ADMIN, RolesAccess.PIC_REGISTRASI]) && hasValidStatus(data?.status, [StatusPermohonanRegistrasi.PEMBUATAN_DRAFT_SK]) ? false : true}
              >
                {tipe_surat.map((item, index) => (
                  <SelectItem key={item} value={index.toString()}>
                    {item}
                  </SelectItem>
                ))}
              </Select>
              {selectedTipeSurat?.toLowerCase() === "produsen" && (
                <Controller
                  name="provinceId"
                  control={control}
                  rules={{ required: "Pejabat wajib diisi" }}
                  render={({ field, fieldState }) => (
                    <ReactSelect
                      isDisabled={hasValidRole(roles, [RolesAccess.SUPER_ADMIN, RolesAccess.PIC_REGISTRASI]) && hasValidStatus(data?.status, [StatusPermohonanRegistrasi.PEMBUATAN_DRAFT_SK]) ? false : true}
                      label="Provinsi"
                      data={dataProvince}
                      isLoading={isLoadingProvince}
                      value={field.value}
                      defaultValue={field.value}
                      onChange={(selectedOption) => field.onChange(selectedOption.value)}
                      error={fieldState.error && fieldState.error.message}
                    />
                  )}
                />
              )}
              {hasValidRole(roles, [RolesAccess.DIREKTUR, RolesAccess.SUPER_ADMIN]) && (
                <Input
                  {...register("tanggal_surat")}
                  variant="faded"
                  labelPlacement="outside"
                  label="Tanggal Surat"
                  type="date"
                  isDisabled={hasValidStatus(data?.status, [StatusPermohonanRegistrasi.DRAFT_SK_TANDA_TANGAN_DIREKTUR]) ? false : true}
                  isRequired={hasValidRole(roles, [RolesAccess.DIREKTUR, RolesAccess.SUPER_ADMIN])}
                  color={errors.tanggal_surat ? "danger" : "default"}
                  isInvalid={errors.tanggal_surat}
                  errorMessage={errors.tanggal_surat && errors.tanggal_surat.message}
                />
              )}
              {selectedTipeSurat?.toLowerCase() === "import" && (
                <Controller
                  name="status_izin"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      variant="faded"
                      label="Status Izin"
                      placeholder="Pilih"
                      labelPlacement="outside"
                      selectedKeys={field.value ? [field.value] : []} // Tambahkan ini
                      onSelectionChange={(keys) => {
                        const selectedValue = Array.from(keys)[0];
                        field.onChange(selectedValue);
                      }}
                      color={errors.status_izin ? "danger" : "default"}
                      isInvalid={errors.status_izin}
                      errorMessage={errors.status_izin && errors.status_izin.message}
                      isDisabled={hasValidRole(roles, [RolesAccess.SUPER_ADMIN, RolesAccess.PIC_REGISTRASI]) && hasValidStatus(data?.status, [StatusPermohonanRegistrasi.PEMBUATAN_DRAFT_SK]) ? false : true}
                    >
                      {permit.map((item, index) => (
                        <SelectItem key={item} value={item}>
                          {item}
                        </SelectItem>
                      ))}
                    </Select>
                  )}
                />
              )}

              {hasValidRole(roles, [RolesAccess.DIREKTUR, RolesAccess.SUPER_ADMIN]) &&
                hasValidStatus(data?.status, [StatusPermohonanRegistrasi.DRAFT_SK_TANDA_TANGAN_DIREKTUR, StatusPermohonanRegistrasi.KIRIM_INSW, StatusPermohonanRegistrasi.SELESAI]) && (
                  <div className="flex flex-col gap-2">
                    <label className="text-sm">File SK</label>
                    {!data?.file_surat ? (
                      <FormAddSK id={id} mutate={mutate} />
                    ) : (
                      <div className="flex items-center gap-2">
                        <a href={`${BASE_URL}/${data.file_surat}`} target="_blank" rel="noopener noreferrer" className="text-primary-500 underline text-sm">
                          Lihat File SK
                        </a>
                        <FormAddSK id={id} mutate={mutate} />
                      </div>
                    )}
                  </div>
                )}

              <Textarea
                {...register("keterangan_sk")}
                variant="faded"
                label="Keterangan SK"
                color={errors.keterangan_sk ? "danger" : "default"}
                isInvalid={errors.keterangan_sk}
                errorMessage={errors.keterangan_sk && errors.keterangan_sk.message}
                isDisabled={hasValidRole(roles, [RolesAccess.SUPER_ADMIN, RolesAccess.PIC_REGISTRASI]) && hasValidStatus(data?.status, [StatusPermohonanRegistrasi.PEMBUATAN_DRAFT_SK]) ? false : true}
                className="col-span-2"
                labelPlacement="outside"
              />
              <Input
                {...register("tanggal_terbit")}
                variant="faded"
                label="Tanggal Terbit"
                type="date"
                labelPlacement="outside"
                color={errors.tanggal_terbit ? "danger" : "default"}
                isInvalid={errors.tanggal_terbit}
                errorMessage={errors.tanggal_terbit && errors.tanggal_terbit.message}
                className="col-span-2"
                isDisabled={hasValidRole(roles, [RolesAccess.SUPER_ADMIN, RolesAccess.PIC_REGISTRASI]) && hasValidStatus(data?.status, [StatusPermohonanRegistrasi.PEMBUATAN_DRAFT_SK]) ? false : true}
              />
              <Input
                {...register("berlaku_dari")}
                isDisabled={hasValidRole(roles, [RolesAccess.SUPER_ADMIN, RolesAccess.PIC_REGISTRASI]) && hasValidStatus(data?.status, [StatusPermohonanRegistrasi.PEMBUATAN_DRAFT_SK]) ? false : true}
                variant="faded"
                label="Berlaku Dari"
                type="date"
                labelPlacement="outside"
                isRequired={!(data?.sub_layanan === "119" || selectedTipeSurat?.toLowerCase() === "produsen")}
                color={errors.berlaku_dari ? "danger" : "default"}
                isInvalid={errors.berlaku_dari}
                errorMessage={errors.berlaku_dari && errors.berlaku_dari.message}
              />
              <Controller
                name="berlaku_sampai"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    variant="faded"
                    labelPlacement="outside"
                    label="Sampai"
                    type="date"
                    isRequired={!(data?.sub_layanan === "119" || selectedTipeSurat?.toLowerCase() === "produsen")}
                    color={errors.berlaku_sampai ? "danger" : "default"}
                    isInvalid={errors.berlaku_sampai}
                    errorMessage={errors.berlaku_sampai && errors.berlaku_sampai.message}
                    isDisabled={hasValidRole(roles, [RolesAccess.SUPER_ADMIN, RolesAccess.PIC_REGISTRASI]) && hasValidStatus(data?.status, [StatusPermohonanRegistrasi.PEMBUATAN_DRAFT_SK]) ? false : true}
                  />
                )}
              />
              <Input
                {...register("nomor_notifikasi_impor")}
                isDisabled={hasValidRole(roles, [RolesAccess.SUPER_ADMIN, RolesAccess.PIC_REGISTRASI]) && hasValidStatus(data?.status, [StatusPermohonanRegistrasi.PEMBUATAN_DRAFT_SK]) ? false : true}
                variant="faded"
                labelPlacement="outside"
                type="text"
                label="Nomor Notifikasi Impor"
                placeholder={"..."}
                color={errors.nomor_notifikasi_impor ? "danger" : "default"}
                isInvalid={errors.nomor_notifikasi_impor}
                errorMessage={errors.nomor_notifikasi_impor && errors.nomor_notifikasi_impor.message}
                className="col-span-2"
              />
              {data?.status === StatusPermohonanRegistrasi.PEMBUATAN_DRAFT_SK && (
                <div className="col-span-2">
                  <Switch isSelected={useSrikandi} onValueChange={setUseSrikandi} size="sm">
                    <span className="text-sm">Gunakan Format Srikandi</span>
                  </Switch>
                </div>
              )}
              <div className={"col-span-2"}>
                <Controller
                  name="pejabat_id"
                  control={control}
                  rules={{
                    required: "Pejabat wajib diisi",
                    validate: (value) => {
                      // Validasi tambahan untuk memastikan object lengkap
                      if (!value || typeof value !== "object" || !value.value) {
                        return "Pejabat wajib dipilih";
                      }
                      return true;
                    },
                  }}
                  render={({ field, fieldState }) => (
                    <ReactSelect
                      isDisabled={hasValidRole(roles, [RolesAccess.SUPER_ADMIN, RolesAccess.PIC_REGISTRASI]) && hasValidStatus(data?.status, [StatusPermohonanRegistrasi.PEMBUATAN_DRAFT_SK]) ? false : true}
                      label="Pejabat"
                      data={dataPejabat}
                      isLoading={isLoadingPejabat}
                      value={field.value}
                      defaultValue={field.value}
                      onChange={(selectedOption) => {
                        // Perbaikan: Simpan object lengkap, bukan hanya value
                        field.onChange(selectedOption || null);
                      }}
                      error={fieldState.error && fieldState.error.message}
                    />
                  )}
                />
              </div>
            </div>
            <div>
              <Button type={"submit"} color="primary" isDisabled={isSubmitting || Object.keys(dirtyFields).length === 0} isLoading={isSubmitting}>
                Simpan
              </Button>
              {(roles.includes(RolesAccess.SUPER_ADMIN) || roles.includes(RolesAccess.DIREKTUR)) &&
                ![StatusPermohonanRegistrasi.SELESAI, StatusPermohonanRegistrasi.DITOLAK, StatusPermohonanRegistrasi.VALIDASI_PEMOHONAN, StatusPermohonanRegistrasi.PEMBUATAN_DRAFT_SK, StatusPermohonanRegistrasi.KIRIM_INSW].includes(
                  data.status
                ) && (
                  <Button
                    color="danger"
                    onPress={onSendToPIC} // Open the modal when clicked
                    className="ml-4"
                  >
                    Kembalikan SK Ke PIC Registrasi
                  </Button>
                )}
            </div>
          </CardBody>
        </Card>
      </form>
      <ModalAlert
        heading="Submit Draft SK ke Direktur?"
        description="Pastikan semua data sudah benar"
        buttonSubmitText="Ya"
        icon="warning"
        onSubmit={() => onSubmitDirektur(id)}
        isOpen={isOpenModalAlert}
        onOpenChange={onOpenChangeModalAlert}
      />

      <Modal isOpen={isOpen} onOpenChange={onOpenChange} onClose={onClose} isDismissable={false} isKeyboardDismissDisabled={false}>
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader>Mengembalikan SK Ke PIC Rekomendasi</ModalHeader>
              <ModalBody>
                <form onSubmit={handleSubmitModal(submitStatusKembalikanKePIC)}>
                  <div className="flex flex-col gap-3 mb-6">
                    <Textarea
                      {...registerModal("notes", { required: "Catatan wajib diisi" })}
                      isRequired={true}
                      variant="faded"
                      type="text"
                      label="Catatan"
                      color={errorsModal.notes ? "danger" : "default"}
                      isInvalid={!!errorsModal.notes}
                      errorMessage={errorsModal.notes?.message}
                    />
                  </div>
                  <div className="flex items-center gap-1">
                    <Button isLoading={isSubmitting} isDisabled={isSubmitting} type="submit" color="primary">
                      Simpan
                    </Button>
                    <Button isDisabled={isSubmitting} color="danger" variant="faded" onPress={onClose}>
                      Batal
                    </Button>
                  </div>
                </form>
              </ModalBody>
              <ModalFooter></ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </div>
  );
}
